import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LaunchIcon from "@mui/icons-material/Launch";
import { deleteIcon } from "../../../assets/images";
import { resolveSectionLetter } from "../../../utils/sectionUtils";
import { TeacherNotificationItem } from "../../../services/notifications/notification.service";
import { useExerciseStore } from "../../../utils/exerciseStore";
import { RouterConstant } from "../../../routers";

interface NotificationCardProps {
  notification: TeacherNotificationItem;
  onMarkAsRead: (id: number | string) => void;
  onDelete: (id: number | string) => void;
  onSelect: (notification: TeacherNotificationItem) => void;
}

export const NotificationCard: React.FC<NotificationCardProps> = ({
  notification,
  onMarkAsRead,
  onDelete,
  onSelect,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { ROUTERS_PATH } = RouterConstant;
  const setSelectedExerciseId = useExerciseStore((s) => s.setSelectedExerciseId);

  const isUnread = !notification.is_read;

  // Format relative or date string
  const formatNotificationTime = (dateStr: string): string => {
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return "";

      const now = new Date();
      const isToday =
        date.getDate() === now.getDate() &&
        date.getMonth() === now.getMonth() &&
        date.getFullYear() === now.getFullYear();

      const yesterday = new Date(now);
      yesterday.setDate(now.getDate() - 1);
      const isYesterday =
        date.getDate() === yesterday.getDate() &&
        date.getMonth() === yesterday.getMonth() &&
        date.getFullYear() === yesterday.getFullYear();

      const timeStr = date.toLocaleTimeString("he-IL", {
        hour: "2-digit",
        minute: "2-digit",
      });

      if (isToday) {
        return `${t("today")}, ${timeStr}`;
      }
      if (isYesterday) {
        return `${t("yesterday")}, ${timeStr}`;
      }

      return date.toLocaleDateString("he-IL", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  const handleCardClick = () => {
    if (isUnread) {
      onMarkAsRead(notification.id);
    }
    onSelect(notification);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(notification.id);
  };

  const resolveExerciseIdFromNotification = (): number | null => {
    const meta = notification.metadata || {};
    if (meta.exercise_id) return Number(meta.exercise_id);
    if (Array.isArray(meta.exercise_ids) && meta.exercise_ids.length > 0) {
      return Number(meta.exercise_ids[0]);
    }
    const rawUrl = meta.action_url || notification.deep_link || meta.deep_link;
    if (typeof rawUrl === "string" && rawUrl.includes("exerciseId=")) {
      const match = rawUrl.match(/[?&]exerciseId=(\d+)/);
      if (match?.[1]) return Number(match[1]);
    }
    return null;
  };

  const handleActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isUnread) {
      onMarkAsRead(notification.id);
    }
    const resolvedExerciseId = resolveExerciseIdFromNotification();
    if (resolvedExerciseId && !isNaN(resolvedExerciseId)) {
      setSelectedExerciseId(resolvedExerciseId);
      navigate(ROUTERS_PATH.DISPLAYEXERCISE, {
        state: {
          exerciseId: resolvedExerciseId,
          from: "notifications",
        },
      });
      return;
    }
    const actionUrl = notification.metadata?.action_url;
    if (actionUrl) {
      navigate(actionUrl);
    }
  };

  const initials =
    notification.sender_initials ||
    (notification.sender_name
      ? notification.sender_name
          .trim()
          .split(" ")
          .map((part) => part[0])
          .slice(0, 2)
          .join("")
      : t("teacherInitialsFallback"));

  const actionUrl = notification.metadata?.action_url;
  const sectionLetter = resolveSectionLetter(notification.metadata);
  const exerciseTitle = notification.metadata?.exercise_title || notification.exercise_name;

  return (
    <div
      className={`notification-card ${
        isUnread ? "notification-card--unread" : "notification-card--read"
      }`}
      onClick={handleCardClick}
      role="article"
      tabIndex={0}
      aria-label={notification.body}
    >
      {/* Avatar */}
      {notification.sender_avatar_url ? (
        <img
          src={notification.sender_avatar_url}
          alt={notification.sender_name || "avatar"}
          className="notification-card__avatar"
        />
      ) : (
        <div className="notification-card__avatar v2-user-message__avatar-placeholder">
          <span>{initials}</span>
        </div>
      )}

      {/* Content Area */}
      <div className="notification-card__content">
        <div className="notification-card__header-row">
          <span className="notification-card__sender-name">
            {notification.sender_name || t("teacher")}
          </span>

          <div className="notification-card__quick-actions">
            <span className="notification-card__timestamp">
              {formatNotificationTime(notification.created_at)}
            </span>
            <button
              type="button"
              className="notification-card__action-icon-btn notification-card__action-icon-btn--delete"
              title={t("delete")}
              onClick={handleDeleteClick}
              aria-label={t("delete")}
            >
              <img
                src={deleteIcon}
                alt={t("delete")}
                className="notification-card__delete-icon"
              />
            </button>
          </div>
        </div>

        {/* Exercise Review Badge & Title */}
        {notification.notification_type === "exercise_review" && (
          <div className="notification-card__review-badge-row">
            <span className="notification-card__type-badge">
              {sectionLetter
                ? t("exerciseReviewSectionBadge", { letter: sectionLetter })
                : t("exerciseReviewNotification")}
            </span>
            {exerciseTitle && (
              <span className="notification-card__exercise-name">
                {t("exerciseReviewInExercise", { exercise: exerciseTitle })}
              </span>
            )}
          </div>
        )}

        {/* Message Body */}
        <p className="notification-card__body-text">{notification.body}</p>

        {/* Optional Action Deep Link */}
        {actionUrl && (
          <div className="notification-card__footer-action">
            <button
              type="button"
              className="notification-card__deep-link-btn"
              onClick={handleActionClick}
            >
              <span>{t("goToExercise")}</span>
              <LaunchIcon className="link-icon" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
